/* The 5-step "Guide" checklist for people who have never built a dashboard.
 * Steps 1-2 are detected from the report itself; 3-5 are remembered per report (they are things the person did, not data).
 * Pure: see web/scripts/guide.check.ts. */
import { isBarWidget, type SDef } from "./model";

export type GuideStepId = "data" | "visual" | "style" | "preview" | "save";
export interface GuideStep { id: GuideStepId; title: string; help: string; done: boolean }
export type GuideFlags = Partial<Record<"style" | "preview" | "save", boolean>>;

export function hasData(def: SDef): boolean {
  const model = (def.model as { tables?: string[] } | undefined)?.tables ?? [];
  return model.length > 0 || def.pages.some((p) => p.widgets.some((w) => !!w.datasetId));
}

export function hasVisual(def: SDef): boolean {
  return def.pages.some((p) => p.widgets.some((w) => !isBarWidget(w) && (w.type === "text" || w.type === "divider" || w.type === "image" ? false : true)));
}

/** A look was picked or a visual was styled (title/card), compared with the report as it was opened. */
export function looksStyled(def: SDef, openedTheme: string | undefined): boolean {
  if ((def.theme ?? "") !== (openedTheme ?? "")) return true;
  return def.pages.some((p) => p.widgets.some((w) => !!w.display?.cardStyle || !!w.display?.titleStyle));
}

export function guideSteps(def: SDef, flags: GuideFlags, openedTheme?: string): GuideStep[] {
  return [
    { id: "data", title: "Pick your data", help: "Choose the file or SharePoint site the numbers come from.", done: hasData(def) },
    { id: "visual", title: "Add a visual", help: "Let the designer suggest charts, or drag a column onto the page.", done: hasVisual(def) },
    { id: "style", title: "Style it", help: "Choose a Look (colours and fonts) for the whole report.", done: !!flags.style || looksStyled(def, openedTheme) },
    { id: "preview", title: "Preview it", help: "See it the way people will see it, on a TV.", done: !!flags.preview },
    { id: "save", title: "Save a version or ask to publish", help: "Your work saves itself as a draft. Publishing makes it live for viewers.", done: !!flags.save },
  ];
}

export function nextStep(steps: GuideStep[]): GuideStep | null {
  return steps.find((s) => !s.done) ?? null;
}
